<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useTheme } from '../composables/useTheme'

useTheme()

const profile = ref({
  name: '魔法代码',
  nickname: '逆行者',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=reverse-time',
  bio: '热爱编程，喜欢探索新技术',
  email: 'admin@160621.xyz',
  website: 'https://www.160621.xyz'
})

const projects = ref([
  {
    name: '博客',
    path: '/blog/',
    icon: '📝',
    color: 'from-blue-500 to-blue-600',
    desc: '技术博客，分享学习心得'
  },
  {
    name: 'API文档',
    path: '/apidoc/',
    icon: '📖',
    color: 'from-green-500 to-green-600',
    desc: 'API接口文档说明'
  },
  {
    name: '音乐',
    path: '/music/',
    icon: '🎵',
    color: 'from-pink-500 to-pink-600',
    desc: '在线音乐播放器'
  },
  {
    name: '书签',
    path: '/bookmark/',
    icon: '🔖',
    color: 'from-orange-500 to-orange-600',
    desc: '个人书签管理'
  },
  {
    name: '360壁纸',
    path: '/360img',
    icon: '🖼️',
    color: 'from-sky-500 to-cyan-600',
    desc: '360壁纸分类浏览与预览',
    single: true
  },
  {
    name: '图片处理',
    path: '/imgtool2/',
    icon: '🛠️',
    color: 'from-violet-500 to-purple-600',
    desc: '图片工具集',
    single: true
  },
  {
    name: '抖音',
    path: '/douyin/',
    icon: '🎬',
    color: 'from-slate-700 to-rose-500',
    desc: '抖音视频下载'
  }
])

// 鼠标跟随光晕
const cursorX = ref(-100)
const cursorY = ref(-100)
let targetX = -100
let targetY = -100
let rafId: number | null = null

function updateCursor() {
  const ease = 0.15
  cursorX.value += (targetX - cursorX.value) * ease
  cursorY.value += (targetY - cursorY.value) * ease
  rafId = requestAnimationFrame(updateCursor)
}

function handleMouseMove(e: MouseEvent) {
  targetX = e.clientX
  targetY = e.clientY
}

const typingText = ref('')
const phrases = ['全栈开发者', '技术爱好者', '终身学习者']
let currentPhraseIndex = 0
let currentCharIndex = 0
let isDeleting = false
function typeWriter() {
  const currentPhrase = phrases[currentPhraseIndex]
  if (isDeleting) {
    typingText.value = currentPhrase.substring(0, currentCharIndex - 1)
    currentCharIndex--
  } else {
    typingText.value = currentPhrase.substring(0, currentCharIndex + 1)
    currentCharIndex++
  }
  let speed = isDeleting ? 50 : 100
  if (!isDeleting && currentCharIndex === currentPhrase.length) {
    speed = 2000
    isDeleting = true
  } else if (isDeleting && currentCharIndex === 0) {
    isDeleting = false
    currentPhraseIndex = (currentPhraseIndex + 1) % phrases.length
    speed = 400
  }
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  setTimeout(typeWriter, speed)
}

onMounted(() => {
  setTimeout(typeWriter, 500)
  updateCursor()
  window.addEventListener('mousemove', handleMouseMove)
})

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId)
  window.removeEventListener('mousemove', handleMouseMove)
})

// SPA 内部路由导航（用于已并入主应用的项目页面）
function navigate(e: MouseEvent, project: any) {
  if (project.single) {
    e.preventDefault()
    history.pushState({}, '', project.path)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
}
</script>

<template>
  <div class="min-h-screen w-full bg-gradient-to-br from-slate-100 via-blue-50/40 to-purple-50/60 dark:from-slate-900 dark:via-slate-800/90 dark:to-purple-950/30 transition-colors duration-300 p-3 sm:p-4 lg:p-5 flex items-center justify-center relative">
    <!-- 鼠标跟随光晕 -->
    <div
      class="fixed pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-purple-500/30 via-pink-400/25 to-cyan-400/20 blur-2xl transition-none"
      :style="{ left: cursorX + 'px', top: cursorY + 'px', width: '140px', height: '140px' }"
    ></div>
    <!-- 背景光晕 -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-[-10%] left-[-5%] w-[45%] h-[45%] bg-blue-400/20 dark:bg-blue-600/15 rounded-full blur-3xl"></div>
      <div class="absolute bottom-[-10%] right-[-5%] w-[45%] h-[45%] bg-purple-400/20 dark:bg-purple-600/15 rounded-full blur-3xl"></div>
      <div class="absolute top-[40%] left-[50%] -translate-x-1/2 w-[35%] h-[35%] bg-cyan-300/10 dark:bg-cyan-500/8 rounded-full blur-3xl"></div>
    </div>

    <!-- 主容器 -->
    <div class="relative z-10 w-full max-w-7xl mx-auto min-h-full">
      <div class="min-h-full grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-3 sm:gap-4 lg:gap-5 items-center">

        <!-- ===== 左侧：个人信息 ===== -->
        <div class="flex items-center justify-center">
          <div class="bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 dark:shadow-black/20 border border-white/50 dark:border-white/10 w-full max-w-sm p-5 sm:p-6 lg:p-7 flex flex-col items-center text-center">
            <!-- 头像 -->
            <div class="relative mb-4">
              <div class="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-[3px] shadow-lg shadow-purple-500/25">
                <img :src="profile.avatar" :alt="profile.name" class="w-full h-full rounded-full bg-white dark:bg-slate-800 object-cover" />
              </div>
              <div class="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md border-2 border-white dark:border-slate-800">✓</div>
            </div>

            <!-- 名字 -->
            <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-0.5">
              {{ profile.name }}
            </h1>

            <!-- 打字机 -->
            <div class="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-3 h-6 flex items-center justify-center gap-0.5">
              <span>{{ typingText }}</span>
            </div>

            <!-- 简介 -->
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
              {{ profile.bio }}
            </p>

            <!-- 分割线 -->
            <div class="w-full h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-slate-600 to-transparent my-4"></div>

            <!-- 社交链接 -->
            <div class="flex gap-4 justify-center">
              <a href="https://github.com/magiccode1412" target="_blank" class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200" title="GitHub">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                GitHub
              </a>
              <a href="https://cnb.cool/magiccode1412" target="_blank" class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200" title="云原生构建">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>
                CNB
              </a>
            </div>

            <!-- 底部版权 -->
            <p class="mt-4 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
              © {{ new Date().getFullYear() }} {{ profile.name }}
            </p>
          </div>
        </div>

        <!-- ===== 右侧：项目网格 ===== -->
        <div class="flex items-center justify-center pb-1">
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 sm:gap-4 w-full max-w-2xl mx-auto">
            <a
              v-for="(project, idx) in projects"
              :key="project.name"
              :href="project.path"
              :data-index="idx"
              @click="navigate($event, project)"
              :data-color="project.color"
              class="card-glow group bg-white/65 dark:bg-slate-800/55 backdrop-blur-lg rounded-xl sm:rounded-2xl shadow-lg shadow-black/4 dark:shadow-black/15 border border-white/50 dark:border-white/[0.08] px-4 py-3 sm:p-5 lg:p-6 flex flex-row sm:flex-col items-center text-left sm:text-center hover:-translate-y-1 hover:scale-[1.02] transition-all duration-300 cursor-pointer relative overflow-hidden gap-3 sm:gap-0"
            >
              <!-- 外部四周发光 -->
              <div :class="`absolute -inset-4 rounded-[inherit] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${project.color} blur-2xl -z-10`"></div>

              <!-- 图标 -->
              <div :class="`relative z-10 w-9 h-9 sm:w-12 sm:h-12 xl:w-16 xl:h-16 rounded-lg sm:rounded-xl sm:rounded-2xl bg-gradient-to-br ${project.color} flex items-center justify-center text-xl sm:text-2xl sm:text-3xl xl:text-4xl shrink-0 sm:mb-3 sm:mb-4 shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-300`">
                {{ project.icon }}
              </div>

              <!-- 文字区 -->
              <div :class="`relative z-10 flex flex-col sm:items-center min-w-0 flex-1`">
                <!-- 名称 -->
                <h3 class="text-sm font-semibold sm:text-base xl:text-lg font-bold text-gray-800 dark:text-white mb-0.5 sm:mb-1 group-hover:text-white transition-colors duration-300 truncate">
                  {{ project.name }}
                </h3>

                <!-- 描述 -->
                <p class="text-xs text-gray-500 dark:text-gray-400 group-hover:text-white/80 transition-colors duration-300 line-clamp-1 sm:line-clamp-2 leading-relaxed hidden sm:block">
                  {{ project.desc }}
                </p>
              </div>
            </a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.rounded-inherit {
  border-radius: inherit;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
